import { useId, useState } from "react";
import { Download } from "lucide-react";
import type { DesktopInfo } from "@goalhorns/shared";
import { paths } from "../../lib/endpoints";
import { useI18n } from "../../i18n/I18nProvider";
import { useDesktopDownload } from "./queries";

const BYTES_PER_MB = 1_000_000;

const HERO_CLASS =
  "inline-flex min-h-12 w-full items-center gap-3 rounded-control border border-edge bg-board px-5 py-2 text-left font-semibold text-ink transition-colors duration-[120ms] hover:bg-ice-2";
const COMPACT_CLASS =
  "inline-flex min-h-11 items-center gap-2 rounded-control px-2 font-semibold text-ink transition-colors duration-[120ms] hover:text-blue focus-visible:text-blue";
const DISCLOSURE_CLASS =
  "inline-flex min-h-11 items-center self-start rounded-control px-1 text-note font-semibold text-blue transition-colors duration-[120ms] hover:text-blue-deep";

export interface DownloadAppButtonProps {
  variant: "hero" | "compact";
  className?: string;
}

/** Plain link to the desktop zip. Renders nothing when the server has no release. */
export function DownloadAppButton({ variant, className = "" }: DownloadAppButtonProps) {
  const info = useDesktopDownload().data;
  if (!info) return null;
  if (variant === "compact") {
    return <CompactLink className={className} />;
  }
  return <HeroLink info={info} className={className} />;
}

function CompactLink({ className }: { className: string }) {
  const { t } = useI18n();
  return (
    <a href={paths.desktopDownload} download className={`${COMPACT_CLASS} ${className}`}>
      <Download size={18} strokeWidth={1.75} aria-hidden="true" />
      {t("download.compact")}
    </a>
  );
}

/** Hero download: the link, and "How to open it" (a disclosure) that shows the unsigned note below. */
function HeroLink({ info, className }: { info: DesktopInfo; className: string }) {
  const { t } = useI18n();
  const noteId = useId();
  const [open, setOpen] = useState(false);
  const sizeMb = Math.round(info.size / BYTES_PER_MB);
  return (
    <div className={`flex w-full flex-col items-stretch gap-2 ${className}`}>
      <a href={paths.desktopDownload} download className={HERO_CLASS}>
        <Download size={18} strokeWidth={1.75} aria-hidden="true" />
        <span className="flex min-w-0 flex-col">
          <span className="text-lg leading-tight whitespace-nowrap">{t("download.hero")}</span>
          <span className="text-note font-medium text-ink-2">
            {t("download.meta", { size: sizeMb, version: info.version })}
          </span>
        </span>
      </a>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={noteId}
        onClick={() => setOpen((value) => !value)}
        className={DISCLOSURE_CLASS}
      >
        {t("download.howToOpen")}
      </button>
      {open ? (
        <p id={noteId} className="text-note text-ink-2">
          {t("download.unsigned")}
        </p>
      ) : null}
    </div>
  );
}
